import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ProgressBar from '../../../components/ui/ProgressBar';
import { checklistService } from '../../../services/checklistService';


const ChecklistCompletion = ({ checklistId, siteId, companyId, onComplete }) => {
  const [checklist, setChecklist] = useState(null);
  const [responses, setResponses] = useState({});
  const [signature, setSignature] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [currentItemForPhoto, setCurrentItemForPhoto] = useState(null);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [gpsError, setGpsError] = useState('');
  const signatureRef = useRef(null);

  useEffect(() => {
    if (checklistId) {
      loadChecklist();
      requestGPSLocation();
    }
  }, [checklistId]);

  const requestGPSLocation = () => {
    if ('geolocation' in navigator) {
      navigator?.geolocation?.getCurrentPosition(
        (position) => {
          setGpsLocation({
            latitude: position?.coords?.latitude,
            longitude: position?.coords?.longitude,
            accuracy: position?.coords?.accuracy
          });
          setGpsError('');
        },
        (error) => {
          console.error('GPS error:', error);
          setGpsError('GPS location unavailable. Checklist completion will proceed without location data.');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      setGpsError('GPS not supported on this device');
    }
  };

  const loadChecklist = async () => {
    try {
      const result = await checklistService?.getChecklistById(checklistId);
      if (result?.data) {
        setChecklist(result?.data);
        const initialResponses = {};
        result?.data?.checklist_items?.forEach(item => {
          initialResponses[item?.id] = {
            status: 'pending',
            comment: '',
            photoFile: null,
            photoPath: null,
            capturedAt: null,
            latitude: null,
            longitude: null
          };
        });
        setResponses(initialResponses);
      }
    } catch (err) {
      console.error('Error loading checklist:', err);
    }
  };

  const handleStatusChange = (itemId, status) => {
    const currentResponse = responses?.[itemId];
    
    // If changing to pass/fail/na and no photo exists, open photo modal
    if (status !== 'pending' && !currentResponse?.photoFile && !currentResponse?.photoPath) {
      setCurrentItemForPhoto(itemId);
      setPhotoModalOpen(true);
      // Temporarily set status - will be confirmed after photo capture
      setResponses(prev => ({
        ...prev,
        [itemId]: { 
          ...prev?.[itemId], 
          status,
          capturedAt: new Date()?.toISOString(),
          latitude: gpsLocation?.latitude || null,
          longitude: gpsLocation?.longitude || null
        }
      }));
    } else {
      setResponses(prev => ({
        ...prev,
        [itemId]: { 
          ...prev?.[itemId], 
          status,
          capturedAt: status !== 'pending' ? new Date()?.toISOString() : null,
          latitude: status !== 'pending' ? (gpsLocation?.latitude || null) : null,
          longitude: status !== 'pending' ? (gpsLocation?.longitude || null) : null
        }
      }));
    }
  };

  const handleCommentChange = (itemId, comment) => {
    setResponses(prev => ({
      ...prev,
      [itemId]: { ...prev?.[itemId], comment }
    }));
  };

  const handlePhotoCapture = async (file) => {
    if (!currentItemForPhoto) return;

    try {
      const uploadResult = await checklistService?.uploadChecklistPhoto(file, companyId, siteId);
      if (uploadResult?.filePath) {
        setResponses(prev => ({
          ...prev,
          [currentItemForPhoto]: { 
            ...prev?.[currentItemForPhoto], 
            photoFile: file,
            photoPath: uploadResult?.filePath 
          }
        }));
      }
    } catch (err) {
      console.error('Error uploading photo:', err);
      setError('Failed to upload photo. Please try again.');
    }
  };

  const openPhotoModal = (itemId) => {
    setCurrentItemForPhoto(itemId);
    setPhotoModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError('');

      const mandatoryItems = checklist?.checklist_items?.filter(item => item?.is_mandatory);
      const incompleteMandatory = mandatoryItems?.some(item => responses?.[item?.id]?.status === 'pending');

      if (incompleteMandatory) {
        setError('Please complete all mandatory checklist items');
        setSubmitting(false);
        return;
      }

      // Validate that all completed items have photo evidence
      const completedItems = Object.entries(responses)?.filter(([_, resp]) => resp?.status !== 'pending');
      const missingPhotos = completedItems?.filter(([_, resp]) => !resp?.photoPath);

      if (missingPhotos?.length > 0) {
        setError('Photo evidence is required for all completed checklist items');
        setSubmitting(false);
        return;
      }

      if (!signature) {
        setError('Digital signature is required');
        setSubmitting(false);
        return;
      }

      const completionResult = await checklistService?.createChecklistCompletion({
        checklistId,
        siteId,
        completionDate: new Date()?.toISOString()?.slice(0, 10),
        signatureData: signature,
        notes
      });

      if (completionResult?.data) {
        const completionId = completionResult?.data?.id;

        for (const itemId of Object.keys(responses)) {
          const response = responses?.[itemId];
          await checklistService?.createChecklistItemResponse({
            completionId,
            checklistItemId: itemId,
            status: response?.status,
            comment: response?.comment,
            photoPath: response?.photoPath,
            capturedAt: response?.capturedAt,
            latitude: response?.latitude,
            longitude: response?.longitude
          });
        }

        onComplete();
      } else {
        setError(completionResult?.error?.message || 'Failed to submit checklist');
      }
    } catch (err) {
      setError('An error occurred while submitting the checklist');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-muted text-muted-foreground',
      pass: 'bg-success/10 text-success border-success',
      fail: 'bg-error/10 text-error border-error',
      na: 'bg-muted text-muted-foreground border-muted-foreground'
    };
    return colors?.[status] || colors?.pending;
  };

  const groupedItems = checklist?.checklist_items?.reduce((acc, item) => {
    if (!acc?.[item?.category]) {
      acc[item?.category] = [];
    }
    acc?.[item?.category]?.push(item);
    return acc;
  }, {});

  if (!checklist) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  const completedCount = Object.values(responses)?.filter(r => r?.status !== 'pending')?.length;
  const totalCount = checklist?.checklist_items?.length || 0;
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="space-y-6">
      {gpsError && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 flex items-start gap-3">
          <Icon name="MapPin" size={20} className="text-warning mt-0.5" />
          <div>
            <p className="text-sm font-medium text-warning">GPS Location Warning</p>
            <p className="text-xs text-muted-foreground mt-1">{gpsError}</p>
          </div>
        </div>
      )}
      {gpsLocation && (
        <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-start gap-3">
          <Icon name="MapPin" size={20} className="text-success mt-0.5" />
          <div>
            <p className="text-sm font-medium text-success">GPS Location Captured</p>
            <p className="text-xs text-muted-foreground mt-1">
              Lat: {gpsLocation?.latitude?.toFixed(6)}, Long: {gpsLocation?.longitude?.toFixed(6)}
              {gpsLocation?.accuracy && ` (±${Math.round(gpsLocation?.accuracy)}m)`}
            </p>
          </div>
        </div>
      )}
      <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{checklist?.checklist_name}</h2>
            {checklist?.description && (
              <p className="text-sm text-muted-foreground mt-1">{checklist?.description}</p>
            )}
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-accent">{progressPercentage}%</div>
            <div className="text-xs text-muted-foreground">{completedCount} of {totalCount}</div>
          </div>
        </div>

        <ProgressBar value={progressPercentage} showValue={false} height="h-2" label="" color="primary" />
      </div>
      {Object.entries(groupedItems || {})?.map(([category, items]) => (
        <div key={category} className="bg-card border border-border rounded-lg shadow-elevation-2 p-4 md:p-6">
          <h3 className="text-md font-semibold text-foreground mb-4">{category}</h3>
          <div className="space-y-4">
            {items?.map((item) => (
              <div key={item?.id} className="border border-border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">
                      {item?.item_description}
                      {item?.is_mandatory && <span className="text-destructive ml-1">*</span>}
                    </p>
                  </div>
                  {responses?.[item?.id]?.photoPath && (
                    <div className="ml-2 flex items-center gap-1 text-success">
                      <Icon name="Camera" size={16} />
                      <Icon name="Check" size={16} />
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mb-3">
                  {['pass', 'fail', 'na']?.map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => handleStatusChange(item?.id, status)}
                      className={`px-4 py-2 rounded-md text-sm font-medium border transition-smooth ${
                        responses?.[item?.id]?.status === status
                          ? getStatusColor(status)
                          : 'bg-background text-muted-foreground border-border hover:border-accent'
                      }`}
                    >
                      {status?.toUpperCase()}
                    </button>
                  ))}
                </div>

                {responses?.[item?.id]?.status !== 'pending' && (
                  <div className="space-y-3">
                    {responses?.[item?.id]?.status === 'fail' && (
                      <textarea
                        value={responses?.[item?.id]?.comment}
                        onChange={(e) => handleCommentChange(item?.id, e?.target?.value)}
                        placeholder="Add comment explaining the failure..."
                        rows={2}
                        className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-smooth resize-none"
                      />
                    )}
                    
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openPhotoModal(item?.id)}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                          responses?.[item?.id]?.photoPath
                            ? 'bg-success/10 text-success border border-success/20 hover:bg-success/20' :'bg-accent text-white hover:bg-accent/90'
                        }`}
                      >
                        <Icon name="Camera" size={16} />
                        <span>{responses?.[item?.id]?.photoPath ? 'Photo Added ✓' : 'Add Photo (Required)'}</span>
                      </button>
                      {responses?.[item?.id]?.capturedAt && (
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Icon name="Clock" size={12} />
                          <span>{new Date(responses?.[item?.id]?.capturedAt)?.toLocaleTimeString()}</span>
                        </div>
                      )}
                    </div>

                    {!responses?.[item?.id]?.photoPath && (
                      <div className="bg-warning/10 border border-warning/20 rounded-md p-2 flex items-start gap-2">
                        <Icon name="AlertTriangle" size={14} className="text-warning mt-0.5" />
                        <p className="text-xs text-warning">Photo evidence is mandatory for audit compliance</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-4 md:p-6">
        <h3 className="text-md font-semibold text-foreground mb-4">Sign Off</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Additional Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
              placeholder="Add any additional notes or observations..."
              rows={3}
              className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-smooth resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Digital Signature <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={signature}
              onChange={(e) => setSignature(e?.target?.value)}
              placeholder="Type your full name to sign"
              className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
            />
            <p className="text-xs text-muted-foreground mt-1">
              By signing, you confirm that all checks have been completed accurately
            </p>
          </div>
        </div>
      </div>
      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive rounded-md">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}
      <div className="flex items-center justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => window.history?.back()}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          loading={submitting}
          iconName="CheckCircle"
          iconPosition="left"
        >
          Submit Checklist
        </Button>
      </div>
    </div>
  );
};

export default ChecklistCompletion;